import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, axiosInstance } from '../App';
import BottomNavigation from '../components/BottomNavigation';
import LanguageToggle from '../components/LanguageToggle';
import { LogOut, User, Settings, Lock, Camera, Edit2, Building2, Mail, Phone, Star, AlertTriangle, Calendar as CalendarIcon, Trash2, Bell, ShieldAlert, Tag, MapPin, Heart, Briefcase, ShoppingBag, PawPrint, HelpCircle, Gift } from 'lucide-react';
import InstallAppMenuItem from '../components/InstallAppMenuItem';
import AuthorityMiniDashboard from '../components/AuthorityMiniDashboard';
import useAuthorityProfile from '../hooks/useAuthorityProfile';

const AVATAR_LOG = '[avatar]';
// Resize a File to a max dimension square JPEG and return a base64 data URL.
// Profile pic is rendered at 96x96 max so 512x512 is more than enough and keeps
// the payload tiny (~30-80KB) so the users document stays small.
const resizeImageToDataUrl = (file, maxDim = 512, mime = 'image/jpeg', quality = 0.85) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to decode image'));
      img.onload = () => {
        try {
          const ratio = Math.min(maxDim / img.width, maxDim / img.height, 1);
          const w = Math.max(1, Math.round(img.width * ratio));
          const h = Math.max(1, Math.round(img.height * ratio));
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          // PNGs with alpha → keep PNG; everything else → jpeg for size
          const outMime = (file.type === 'image/png') ? 'image/png' : mime;
          const dataUrl = canvas.toDataURL(outMime, quality);
          resolve(dataUrl);
        } catch (err) { reject(err); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const AuthorityBlock = () => {
  const authority = useAuthorityProfile();
  return <AuthorityMiniDashboard {...authority} variant="profile" />;
};

const Profile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [emailNotifEnabled, setEmailNotifEnabled] = useState(true);
  const [prefsBusy, setPrefsBusy] = useState(false);
  const [prefsToast, setPrefsToast] = useState(null);
  // Iter 135 — self-serve display-name edit (uses existing PUT /user/profile)
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameBusy, setNameBusy] = useState(false);
  const [nameError, setNameError] = useState(null);

  const startEditName = () => {
    setNameDraft(profile?.fullName || '');
    setNameError(null);
    setEditingName(true);
  };
  const cancelEditName = () => {
    setEditingName(false);
    setNameError(null);
  };
  const saveDisplayName = async () => {
    const trimmed = (nameDraft || '').trim();
    if (!trimmed) { setNameError(t('nameRequired', 'Name is required.')); return; }
    if (trimmed.length > 80) { setNameError(t('nameTooLong', 'Please keep it under 80 characters.')); return; }
    if (trimmed === (profile?.fullName || '')) { setEditingName(false); return; }
    setNameBusy(true);
    setNameError(null);
    try {
      await axiosInstance.put('/user/profile', null, { params: { fullName: trimmed } });
      setProfile((p) => (p ? { ...p, fullName: trimmed } : p));
      setEditingName(false);
      setPrefsToast({ ok: true, msg: t('displayNameSaved', 'Display name updated.') });
      setTimeout(() => setPrefsToast(null), 4000);
    } catch (e) {
      setNameError(e?.response?.data?.detail || t('displayNameSaveFailed', 'Could not update your name. Please try again.'));
    } finally {
      setNameBusy(false);
    }
  };
  const fileInputRef = useRef(null);

  // Cache key kept per-user so multiple accounts on the same device don't
  // collide. Backend is the source of truth — this is just for instant paint.
  const profileImageKey = `profileImage_${user?.userId || 'default'}`;

  // 1) Instant paint from cache, then 2) reconcile against backend.
  useEffect(() => {
    try {
      const cached = localStorage.getItem(profileImageKey);
      if (cached) {
        console.log(`${AVATAR_LOG} cache hit — painting instantly (${cached.length} chars)`);
        setProfileImage(cached);
      } else {
        setProfileImage(null);
      }
    } catch (_) { /* noop */ }

    (async () => {
      try {
        console.log(`${AVATAR_LOG} GET /api/user/avatar — reconciling with server`);
        const res = await axiosInstance.get('/user/avatar');
        const serverAvatar = res?.data?.avatarUrl || null;
        console.log(`${AVATAR_LOG} server says: ${serverAvatar ? `avatar present (${serverAvatar.length} chars)` : 'no avatar'}`);
        setProfileImage(serverAvatar);
        try {
          if (serverAvatar) localStorage.setItem(profileImageKey, serverAvatar);
          else localStorage.removeItem(profileImageKey);
        } catch (_) { /* quota — ignore */ }
      } catch (err) {
        console.warn(`${AVATAR_LOG} GET failed (keeping cache):`, err?.response?.status, err?.message);
      }
    })();
  }, [profileImageKey]);

  useEffect(() => {
    fetchProfile();
    (async () => {
      try {
        const res = await axiosInstance.get('/user/notification-preferences');
        setEmailNotifEnabled(res?.data?.emailEnabled !== false);
      } catch (e) {
        // Default true on failure — non-critical
        console.warn('[prefs] load failed:', e?.message);
      }
    })();
  }, []);

  const togglePrefs = async (next) => {
    if (prefsBusy) return;
    setPrefsBusy(true);
    setEmailNotifEnabled(next);  // optimistic
    try {
      await axiosInstance.put('/user/notification-preferences', { emailEnabled: next });
      setPrefsToast({ ok: true, msg: next ? t('emailNotificationsSavedOn', 'Email notifications turned on.') : t('emailNotificationsSavedOff', "Email notifications turned off. You'll still receive critical and billing emails.") });
      setTimeout(() => setPrefsToast(null), 4000);
    } catch (e) {
      // revert
      setEmailNotifEnabled(!next);
      setPrefsToast({ ok: false, msg: e?.response?.data?.detail || 'Could not save preference. Please try again.' });
      setTimeout(() => setPrefsToast(null), 4000);
    } finally {
      setPrefsBusy(false);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const userResponse = await axiosInstance.get('/user/profile');
      setProfile(userResponse.data);

      // If business user, fetch business profile
      if (user?.role === 'business') {
        try {
          const bizResponse = await axiosInstance.get('/business/me');
          setBusinessProfile(bizResponse.data);
        } catch (e) {
          console.log('No business profile found');
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleImageClick = () => {
    if (avatarBusy) return;
    console.log(`${AVATAR_LOG} camera/avatar clicked — opening file picker`);
    fileInputRef.current?.click();
  };

  const handleDeleteImage = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    console.log(`${AVATAR_LOG} trash clicked — handler fired`);
    if (!profileImage) {
      console.log(`${AVATAR_LOG} no image to delete — aborting`);
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDeleteImage = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (avatarBusy) return;
    setAvatarBusy(true);
    console.log(`${AVATAR_LOG} confirm clicked — calling DELETE /api/user/avatar`);
    try {
      const res = await axiosInstance.delete('/user/avatar');
      console.log(`${AVATAR_LOG} DELETE response:`, res?.status, res?.data);
      // Clear UI + cache only after server confirms
      setProfileImage(null);
      try { localStorage.removeItem(profileImageKey); } catch (_) { /* noop */ }
      if (fileInputRef.current) fileInputRef.current.value = '';
      setShowDeleteConfirm(false);
      console.log(`${AVATAR_LOG} state + cache cleared — UI updated`);
    } catch (err) {
      console.error(`${AVATAR_LOG} DELETE failed:`, err?.response?.status, err?.response?.data || err?.message);
      alert(t('avatarDeleteFailed', 'Could not remove your profile picture. Please try again.'));
    } finally {
      setAvatarBusy(false);
    }
  };

  const cancelDeleteImage = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    console.log(`${AVATAR_LOG} cancel clicked — keeping image`);
    setShowDeleteConfirm(false);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (avatarBusy) return;
    setAvatarBusy(true);
    console.log(`${AVATAR_LOG} file selected: name=${file.name} type=${file.type} size=${file.size}b`);
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      console.log(`${AVATAR_LOG} resized → ${dataUrl.length} chars data URL — calling POST /api/user/avatar`);
      const res = await axiosInstance.post('/user/avatar', { data: dataUrl });
      console.log(`${AVATAR_LOG} POST response:`, res?.status, { ok: res?.data?.ok, len: res?.data?.avatarUrl?.length });
      const saved = res?.data?.avatarUrl || dataUrl;
      setProfileImage(saved);
      try { localStorage.setItem(profileImageKey, saved); } catch (_) { /* quota — ignore */ }
      // Reset the input so re-selecting the same file later still fires onChange
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      console.error(`${AVATAR_LOG} upload failed:`, err?.response?.status, err?.response?.data || err?.message);
      alert(t('avatarUploadFailed', 'Could not save your profile picture. Please try a smaller image.'));
    } finally {
      setAvatarBusy(false);
    }
  };

  const getInitials = () => {
    if (profile?.fullName) {
      return profile.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user?.role === 'business' ? 'B' : 'U';
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="profile-title">
              <User className="h-6 w-6" />
              {t('nav.profile', 'Profile')}
            </h1>
            <LanguageToggle />
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="animate-pulse">
              <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-32 mx-auto mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-48 mx-auto"></div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Iter 205a — Authority block (officials only). Sits at the
                top so a Town Hall sees publishing CTAs immediately on
                their first login. Renders nothing for residents. */}
            <AuthorityBlock />
            {/* Profile Card */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-purple-100 to-indigo-100 p-8">
                <div className="flex flex-col items-center">
                  {/* Profile Picture — buttons are positioned *outside* the
                      96x96 avatar bounding box (via -bottom-2 / -left-2 / -right-2)
                      so they never share a hit zone with the avatar itself.
                      z-10 + pointer-events-auto keeps them on top of any
                      hover overlay or transient UI layer. */}
                  <div className="relative mb-4 w-28 h-28 flex items-center justify-center">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={handleImageClick}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleImageClick(); } }}
                      className="relative group cursor-pointer rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                      data-testid="profile-image-btn"
                      aria-label={t('changeProfilePicture', 'Change profile picture')}
                    >
                      {profileImage ? (
                        <img
                          src={profileImage}
                          alt="Profile"
                          className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold border-4 border-white shadow-lg">
                          {getInitials()}
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                        <Camera className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      data-testid="profile-image-input"
                    />
                    {/* Camera (change) button — pushed *outside* the avatar's
                        bounding box so it never shares a hit zone with the
                        avatar-click div underneath. */}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleImageClick(); }}
                      disabled={avatarBusy}
                      className="absolute -bottom-1 -right-1 z-10 p-2 bg-purple-600 rounded-full text-white shadow-lg hover:bg-purple-700 transition-colors pointer-events-auto disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="profile-image-change-btn"
                      aria-label={t('changeProfilePicture', 'Change profile picture')}
                    >
                      <Camera className="h-4 w-4" />
                    </button>
                    {/* Delete — only shown when a picture actually exists */}
                    {profileImage && (
                      <button
                        type="button"
                        onClick={handleDeleteImage}
                        disabled={avatarBusy}
                        className="absolute -bottom-1 -left-1 z-10 p-2 bg-red-500 rounded-full text-white shadow-lg hover:bg-red-600 transition-colors pointer-events-auto disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="profile-image-delete-btn"
                        aria-label={t('deleteProfilePicture', 'Delete profile picture')}
                        title={t('deleteProfilePicture', 'Delete profile picture')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Inline confirmation — replaces window.confirm(), which
                      can be silently suppressed inside PWA/standalone browsers
                      and on some desktop Chrome profiles with popups blocked. */}
                  {showDeleteConfirm && (
                    <div
                      className="mb-4 w-full max-w-sm bg-white border border-red-200 rounded-xl shadow-md p-4"
                      data-testid="profile-image-delete-confirm"
                      role="alertdialog"
                      aria-labelledby="delete-pic-confirm-title"
                    >
                      <p id="delete-pic-confirm-title" className="text-sm text-gray-800 mb-3">
                        {t('confirmDeletePicture', 'Remove your profile picture?')}
                      </p>
                      <div className="flex gap-2 justify-end">
                        <button
                          type="button"
                          onClick={cancelDeleteImage}
                          disabled={avatarBusy}
                          className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
                          data-testid="profile-image-delete-cancel-btn"
                        >
                          {t('cancel', 'Cancel')}
                        </button>
                        <button
                          type="button"
                          onClick={confirmDeleteImage}
                          disabled={avatarBusy}
                          className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          data-testid="profile-image-delete-confirm-btn"
                        >
                          {avatarBusy ? t('removing', 'Removing…') : t('remove', 'Remove')}
                        </button>
                      </div>
                    </div>
                  )}

                  {editingName ? (
                    <div className="mt-1 w-full max-w-xs" data-testid="profile-name-edit-form">
                      <input
                        type="text"
                        value={nameDraft}
                        maxLength={80}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveDisplayName();
                          if (e.key === 'Escape') cancelEditName();
                        }}
                        placeholder={t('namePlaceholder', 'Your name')}
                        className="w-full px-3 py-1.5 text-base font-semibold text-gray-900 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                        autoFocus
                        disabled={nameBusy}
                        data-testid="profile-name-input"
                      />
                      <div className="mt-2 flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={saveDisplayName}
                          disabled={nameBusy}
                          className="px-3 py-1.5 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60 transition-colors"
                          data-testid="profile-name-save-btn"
                        >
                          {nameBusy ? t('saving', 'Saving...') : t('save', 'Save')}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditName}
                          disabled={nameBusy}
                          className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                          data-testid="profile-name-cancel-btn"
                        >
                          {t('cancel', 'Cancel')}
                        </button>
                      </div>
                      {nameError && (
                        <p className="mt-1 text-xs text-red-600 text-center" data-testid="profile-name-error">{nameError}</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <h2 className="text-xl font-semibold text-gray-900" data-testid="profile-name">
                        {profile?.fullName || 'User'}
                      </h2>
                      <button
                        type="button"
                        onClick={startEditName}
                        className="p-1 rounded-full text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                        aria-label={t('editName', 'Edit display name')}
                        title={t('editName', 'Edit display name')}
                        data-testid="profile-name-edit-btn"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <p className="text-gray-600 flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    {profile?.email}
                  </p>
                  <span className="mt-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                    {user?.role === 'business' ? t('businessAccount') : user?.role === 'admin' ? t('admin') : t('regularAccount')}
                  </span>
                </div>
              </div>

              {/* Business Info (if business account) */}
              {businessProfile && (
                <div className="p-4 border-t bg-gray-50">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-gray-900">{businessProfile.businessName}</p>
                      <p className="text-sm text-gray-500">{businessProfile.category}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notification preferences — single master toggle */}
            <div className="bg-white rounded-2xl shadow-md p-6" data-testid="notif-prefs-card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('notificationPreferences', 'Notification preferences')}
              </h3>
              <label className="flex items-start justify-between gap-4 cursor-pointer">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{t('emailNotifications', 'Email notifications')}</p>
                  <p className="text-sm text-gray-600 mt-1" data-testid="notif-prefs-desc">
                    {emailNotifEnabled
                      ? t('emailNotificationsOnDesc', "We'll email you about your favourites, helper updates and weekly digests.")
                      : t('emailNotificationsOffDesc', "Off — we'll stop sending updates and digests. You'll still receive critical emails (password reset, account/security, billing receipts) and important paid-service updates.")}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={emailNotifEnabled}
                  disabled={prefsBusy}
                  onClick={() => togglePrefs(!emailNotifEnabled)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${emailNotifEnabled ? 'bg-purple-600' : 'bg-gray-300'} ${prefsBusy ? 'opacity-60 cursor-wait' : ''}`}
                  data-testid="notif-prefs-toggle"
                  data-state={emailNotifEnabled ? 'on' : 'off'}
                >
                  <span aria-hidden className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${emailNotifEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </label>
              {prefsToast && (
                <p
                  className={`mt-3 text-xs ${prefsToast.ok ? 'text-green-700' : 'text-red-700'}`}
                  data-testid="notif-prefs-toast"
                >
                  {prefsToast.msg}
                </p>
              )}
            </div>

            {/* Location alert preferences — Iter 156 */}
            <div className="bg-white rounded-2xl shadow-md p-6" data-testid="location-prefs-card">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">
                    {t('locationPrefs.cardTitle', 'Location alert preferences')}
                  </p>
                  <p className="mt-1 text-sm text-gray-600">
                    {t('locationPrefs.cardBody', 'Choose your town, nearby towns, or the whole Almería province so we send you the right alerts.')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('find2day:open-location-picker'))}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition-colors"
                data-testid="open-location-picker-btn"
              >
                {t('locationPrefs.editCta', 'Edit my location alerts')}
              </button>
            </div>

            {/* Feb 2026 — Profile is management-only. Creation happens from
                Marketplace and the Community Safety Hub. Quick Access below
                is a set of links to view/edit things the user already owns.
                Following remains here (moved out of the bottom nav) — the
                red-dot on the Profile icon still surfaces new updates. */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="quick-access-section">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-bold text-gray-900">{t('quickAccess.title', 'Manage')}</h2>
                <p className="text-[11px] text-gray-500">{t('quickAccess.descManage', 'View and edit what you have posted.')}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 p-3">
                {[
                  { key: 'my-marketplace',   label: t('quickAccess.myMarketplace',  'My Marketplace Listings'), route: '/marketplace?mine=1',      Icon: ShoppingBag, color: 'emerald', testId: 'qa-my-marketplace' },
                  { key: 'my-lost-found',    label: t('quickAccess.myLostFound',    'My Lost & Found'),         route: '/lost-found?mine=1',       Icon: PawPrint,    color: 'cyan',    testId: 'qa-my-lost-found' },
                  { key: 'my-reports',       label: t('quickAccess.myReports',      'My Community Reports'),    route: '/safety-bulletin/mine',    Icon: ShieldAlert, color: 'red',     testId: 'qa-my-reports' },
                  { key: 'my-help',          label: t('quickAccess.myHelp',         'My Help Requests'),        route: '/safety-bulletin/mine?filter=help', Icon: HelpCircle, color: 'amber',   testId: 'qa-my-help' },
                  { key: 'saved',            label: t('quickAccess.savedPosts',     'Saved Posts'),             route: '/favorites',               Icon: Heart,       color: 'rose',    testId: 'qa-saved-posts' },
                  { key: 'following',        label: t('quickAccess.following',      'Following'),               route: '/favorites?tab=following', Icon: Heart,       color: 'purple',  testId: 'qa-following', showDot: true },
                  { key: 'towns',            label: t('quickAccess.towns',          'Followed Towns'),          route: '/followed-towns',          Icon: MapPin,      color: 'cyan',    testId: 'qa-towns' },
                  { key: 'notifications',    label: t('quickAccess.notifications',  'Notifications'),           route: '/notifications',           Icon: Bell,        color: 'purple',  testId: 'qa-notifications' },
                  { key: 'calendar',         label: t('quickAccess.calendar',       'Calendar'),                route: '/calendar',                Icon: CalendarIcon, color: 'violet',  testId: 'qa-calendar' },
                ].map(({ key, label, route, Icon, color, testId, showDot }) => (
                  <button
                    key={key}
                    onClick={() => navigate(route)}
                    data-testid={testId}
                    className="relative flex flex-col items-start gap-2 p-3 rounded-lg border border-gray-100 hover:border-purple-300 hover:bg-purple-50 hover:shadow-sm transition-all text-left"
                  >
                    <div className={`p-1.5 rounded-md ${
                      color === 'red'     ? 'bg-red-100 text-red-600' :
                      color === 'amber'   ? 'bg-amber-100 text-amber-700' :
                      color === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
                      color === 'violet'  ? 'bg-violet-100 text-violet-700' :
                      color === 'rose'    ? 'bg-rose-100 text-rose-600' :
                      color === 'indigo'  ? 'bg-indigo-100 text-indigo-700' :
                      color === 'cyan'    ? 'bg-cyan-100 text-cyan-700' :
                                            'bg-purple-100 text-purple-700'
                    }`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className="text-[13px] font-semibold text-gray-900 leading-tight">{label}</span>
                    {showDot && (
                      <span
                        className="absolute top-2 right-2 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"
                        data-testid={`${testId}-dot`}
                        aria-hidden="true"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-2">
                <button
                  onClick={() => navigate('/my-reviews')}
                  className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 rounded-lg transition-colors text-left"
                  data-testid="my-reviews-btn"
                >
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Star className="h-5 w-5 text-yellow-600" />
                  </div>
                  <span className="text-gray-900 font-medium">{t('myReviews', 'My Reviews')}</span>
                </button>

                <button
                  onClick={() => { let tk=null; try { tk=localStorage.getItem('helper_token'); } catch(e){} navigate(tk ? '/helper/dashboard' : '/affiliate'); }}
                  className="w-full flex items-center gap-3 p-4 hover:bg-purple-50 rounded-lg transition-colors text-left"
                  data-testid="profile-community-partner-btn"
                >
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Gift className="h-5 w-5 text-purple-600" />
                  </div>
                  <span className="text-gray-900 font-medium">{t('communityPartner.title', 'Become a Community Partner')}</span>
                </button>

                {/* Iter 194 — Persistent "Install App" row. Hides automatically
                    when the app is already running in standalone mode. */}
                <InstallAppMenuItem surface="profile" testId="profile-install-app-row" />

                <button
                  onClick={() => navigate('/change-password')}
                  className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 rounded-lg transition-colors text-left"
                  data-testid="change-password-btn"
                >
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Lock className="h-5 w-5 text-purple-600" />
                  </div>
                  <span className="text-gray-900 font-medium">{t('changePassword')}</span>
                </button>

                <button
                  onClick={() => navigate('/settings')}
                  className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 rounded-lg transition-colors text-left"
                  data-testid="settings-btn"
                >
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Settings className="h-5 w-5 text-purple-600" />
                  </div>
                  <span className="text-gray-900 font-medium">{t('settings')}</span>
                </button>

                {/* Iter 210 — quick link removed; Notification Preferences is
                    now in the Quick Access grid above. */}

                {/* Holidays moved into the Calendar area (Calendar → Holidays tab). */}

                <button
                  onClick={() => navigate('/emergency')}
                  className="w-full flex items-center gap-3 p-4 hover:bg-red-50 rounded-lg transition-colors text-left"
                  data-testid="profile-emergency-btn"
                >
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <span className="text-gray-900 font-medium">{t('emergency.title', 'Emergency Numbers')}</span>
                </button>
              </div>
            </div>

            {/* Logout */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 p-4 hover:bg-red-50 transition-colors text-left text-red-600"
                data-testid="profile-logout-button"
              >
                <div className="p-2 bg-red-100 rounded-lg">
                  <LogOut className="h-5 w-5 text-red-600" />
                </div>
                <span className="font-medium">{t('logout')}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default Profile;
